import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Eye, Gift } from "lucide-react";
import { gifts as giftsApi } from "../lib/api";
import SmsConsentDisclosure from "../components/SmsConsentDisclosure";

export default function AuthPage({ mode = "login" }) {
  const { user, login, signup, demoLogin, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/";
  const prefillEmail = searchParams.get("email") || "";
  const giftToken = searchParams.get("gift") || null;
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  // SMS consent must default OFF per Twilio A2P 10DLC. Affirmative user
  // action (checking the box) is required to record consent.
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [gift, setGift] = useState(null); // paid gift preview
  const navigate = useNavigate();

  // If arriving via a gift claim link, fetch the gift to show duration in the
  // banner and to auto-redeem after auth.
  useEffect(() => {
    if (!giftToken) return;
    let cancelled = false;
    giftsApi
      .lookup(giftToken)
      .then((g) => {
        if (!cancelled) setGift(g);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [giftToken]);

  if (loading) return null;
  if (user) return <Navigate to={redirectTo} replace />;

  const inGiftFlow = Boolean(giftToken && gift);
  const giftMonths = gift?.duration_months;
  const giftDurationLabel = giftMonths
    ? `${giftMonths} ${giftMonths === 1 ? "month" : "months"}`
    : "";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        // Only send phone when the user affirmatively opted in. A phone
        // number without consent is not stored (Twilio A2P 10DLC).
        const extras = smsOptIn && phone.trim()
          ? { phone: phone.trim(), sms_opt_in: true }
          : {};
        await signup(email, password, name, extras);
      } else {
        await login(email, password);
      }
      // Gift redemption is best-effort here — if it succeeds, land on Home
      // with the gift active. If it fails (network, race), fall back to the
      // claim page which will let the user retry manually.
      if (giftToken) {
        try {
          const r = await giftsApi.redeem(giftToken);
          if (r.already_redeemed) {
            toast.success("Gift already active on your account");
          } else {
            toast.success(
              giftDurationLabel
                ? `Gift activated — ${giftDurationLabel} of Pro`
                : "Gift activated — welcome to LifeGuide Pro"
            );
          }
          navigate("/");
          return;
        } catch (redeemErr) {
          // Fall through to the claim page for manual retry.
          navigate(`/gift/claim/${giftToken}`);
          return;
        }
      }
      if (mode === "signup") {
        toast.success("Welcome — your 3-day Pro trial is live");
      } else {
        toast.success("Welcome back");
      }
      navigate(redirectTo);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Let's try that again");
    } finally {
      setBusy(false);
    }
  };

  const tryDemo = async () => {
    setDemoBusy(true);
    try {
      await demoLogin();
      toast.success("Demo unlocked — full Pro access");
      navigate(redirectTo);
    } catch {
      toast.error("Could not start demo");
    } finally {
      setDemoBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md fade-in-up">
          <div className="flex items-center gap-2.5 mb-12">
            <div className="w-10 h-10 rounded-2xl bg-[#4F6F52] flex items-center justify-center shadow-[0_4px_14px_rgba(79,111,82,0.25)]">
              <Sparkles size={18} className="text-[#F9FAF7]" strokeWidth={1.8} />
            </div>
            <span className="font-display text-2xl text-[#4F6F52]">LifeGuide</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#2E2E2E] mb-3">
            {inGiftFlow
              ? mode === "signup"
                ? "Activate your gift"
                : "Sign in to activate your gift"
              : mode === "signup"
              ? "Begin your journey"
              : "Welcome home"}
          </h1>
          <p className="text-[#6B6B6B] mb-8 leading-relaxed">
            {inGiftFlow
              ? `You have ${giftDurationLabel} of LifeGuide Pro waiting — already paid for.`
              : mode === "signup"
              ? "A quiet companion for the moments that matter most."
              : "Step back into your guidance."}
          </p>

          {/* Real scenarios this app helps with — sets the tone immediately.
              Hidden in gift flow to keep the page focused on activating. */}
          {!inGiftFlow && (
            <div className="mb-6 rounded-2xl border border-[#DCE5DB] bg-white/60 backdrop-blur-sm p-4">
              <div className="text-xs uppercase tracking-[0.16em] text-[#7FA7B0] font-semibold mb-2.5">
                For moments like
              </div>
              <ul className="space-y-1.5 text-[14px] text-[#2E2E2E] leading-relaxed">
                <li className="flex gap-2"><span className="text-[#4F6F52]">·</span>“I’m furious at my spouse and about to send a message I’ll regret”</li>
                <li className="flex gap-2"><span className="text-[#4F6F52]">·</span>“I just yelled at my kids and feel like a terrible parent”</li>
                <li className="flex gap-2"><span className="text-[#4F6F52]">·</span>“I feel lost and don’t know what to do next”</li>
                <li className="flex gap-2"><span className="text-[#4F6F52]">·</span>“I’m anxious about money and can’t sleep”</li>
              </ul>
            </div>
          )}

          {inGiftFlow ? (
            <div
              data-testid="signup-gift-banner"
              className="mb-6 rounded-2xl border border-[#4F6F52]/30 bg-[#E1ECDD]/70 px-4 py-3.5 flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-xl bg-[#4F6F52]/15 flex items-center justify-center shrink-0">
                <Gift size={16} className="text-[#4F6F52]" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-[#4F6F52] text-sm leading-tight">
                  {mode === "signup"
                    ? `Create your account to activate your ${giftDurationLabel} of LifeGuide Pro`
                    : `Sign in to activate your ${giftDurationLabel} gift`}
                </div>
                <div className="text-xs text-[#4F6F52]/85 leading-snug mt-0.5">
                  Already paid for as a gift. No credit card, no 3-day trial.
                </div>
              </div>
            </div>
          ) : mode === "signup" ? (
            <div
              data-testid="signup-trial-banner"
              className="mb-6 rounded-2xl border border-[#D6C7A1]/60 bg-[#F5EBD9]/70 px-4 py-3.5 flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-xl bg-[#8C7A4F]/15 flex items-center justify-center shrink-0">
                <Gift size={16} className="text-[#8C7A4F]" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-[#8C7A4F] text-sm leading-tight">
                  3-day free trial · Pro access
                </div>
                <div className="text-xs text-[#8C7A4F]/85 leading-snug mt-0.5">
                  No credit card required. Continue for $3.99/month or stay on free after.
                </div>
              </div>
            </div>
          ) : null}

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "signup" && (
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1.5">Name</label>
                <input
                  data-testid="auth-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                  placeholder="Your first name"
                />
              </div>
            )}
            <div>
              <label className="block text-sm text-[#6B6B6B] mb-1.5">Email</label>
              <input
                data-testid="auth-email"
                type="email"
                required
                readOnly={inGiftFlow}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition ${
                  inGiftFlow ? "bg-[#F9FAF7] text-[#6B6B6B] cursor-not-allowed" : ""
                }`}
                placeholder="you@example.com"
              />
              {inGiftFlow && (
                <p className="text-xs text-[#6B6B6B] mt-1.5">
                  This gift is addressed to this email. Sign in or sign up with the same address to activate.
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm text-[#6B6B6B]">Password</label>
                {mode === "login" && (
                  <Link
                    to="/forgot-password"
                    data-testid="auth-forgot-password-link"
                    className="text-xs text-[#3F5A42] hover:text-[#2E4531] font-medium"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <input
                data-testid="auth-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                placeholder="At least 6 characters"
              />
            </div>
            {mode === "signup" && (
              <div
                data-testid="signup-sms-optin-block"
                className="rounded-2xl border border-[#DCE5DB] bg-[#F9FAF7]/70 p-4 space-y-3"
              >
                <div>
                  <label className="block text-sm text-[#6B6B6B] mb-1.5">
                    Mobile number{" "}
                    <span className="text-[#6B6B6B]/70 font-normal">(optional)</span>
                  </label>
                  <input
                    data-testid="auth-phone"
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    data-testid="auth-sms-consent"
                    type="checkbox"
                    checked={smsOptIn}
                    onChange={(e) => setSmsOptIn(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#DCE5DB] text-[#4F6F52] focus:ring-[#4F6F52]/30 accent-[#4F6F52]"
                  />
                  <span className="text-sm text-[#2E2E2E] leading-snug">
                    Yes, send me LifeGuide SMS/text messages.
                  </span>
                </label>
                <SmsConsentDisclosure testId="signup-sms-disclosure" />
              </div>
            )}
            <button
              data-testid="auth-submit"
              type="submit"
              disabled={busy}
              className="w-full bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full py-3.5 font-medium transition-colors disabled:opacity-60 shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
            >
              {busy
                ? "One moment..."
                : inGiftFlow
                ? mode === "signup"
                  ? "Create account & activate gift"
                  : "Sign in & activate gift"
                : mode === "signup"
                ? "Create account"
                : "Sign in"}
            </button>
          </form>

          <div className="my-7 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#DCE5DB]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[#6B6B6B]">or</span>
            <div className="flex-1 h-px bg-[#DCE5DB]" />
          </div>

          <button
            type="button"
            onClick={tryDemo}
            disabled={demoBusy}
            data-testid="demo-mode-btn"
            className="w-full bg-[#F5EBD9] hover:bg-[#efe1c8] text-[#8C7A4F] border border-[#D6C7A1]/60 rounded-full py-3.5 font-medium transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Eye size={16} strokeWidth={1.7} />
            {demoBusy ? "Loading demo..." : "Try the Demo"}
          </button>
          <p className="text-xs text-[#6B6B6B] mt-2.5 text-center">
            Experience LifeGuide instantly · no signup required
          </p>

          <div className="mt-7 text-sm text-[#6B6B6B]">
            {mode === "signup" ? (
              <>
                Already have an account?{" "}
                <Link
                  to={
                    inGiftFlow
                      ? `/login?redirect=/gift/claim/${giftToken}&email=${encodeURIComponent(email)}&gift=${giftToken}`
                      : "/login"
                  }
                  className="text-[#4F6F52] font-medium underline-offset-4 hover:underline"
                  data-testid="link-login"
                >
                  Sign in
                </Link>
              </>
            ) : (
              <>
                New to LifeGuide?{" "}
                <Link
                  to={
                    inGiftFlow
                      ? `/signup?redirect=/gift/claim/${giftToken}&email=${encodeURIComponent(email)}&gift=${giftToken}`
                      : "/signup"
                  }
                  className="text-[#4F6F52] font-medium underline-offset-4 hover:underline"
                  data-testid="link-signup"
                >
                  Create an account
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="text-center text-xs text-[#6B6B6B] py-6 px-4 space-y-1.5">
        <div>LifeGuide · Wisdom · Reflection · Action</div>
        <div className="flex items-center justify-center gap-4">
          <Link to="/privacy" className="hover:text-[#4F6F52]" data-testid="footer-privacy-link">
            Privacy
          </Link>
          <span className="text-[#DCE5DB]">·</span>
          <Link to="/terms" className="hover:text-[#4F6F52]" data-testid="footer-terms-link">
            Terms
          </Link>
          <span className="text-[#DCE5DB]">·</span>
          <Link to="/sms" className="hover:text-[#4F6F52]" data-testid="footer-sms-link">
            SMS Updates
          </Link>
        </div>
      </div>
    </div>
  );
}
